import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const empty = () => ({ profitCenterId: '', type: 'REVENUE', amount: '', description: '', reference: '' });

export function ProfitCenterPostingModal({ open, onClose, centers = [] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.postProfitCenterEntry(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'profit-centers'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.profitCenterId || !form.amount || Number(form.amount) < 0) { setError(t('pc.error.postRequired')); return; }
    save.mutate({ profitCenterId: form.profitCenterId, type: form.type, amount: Number(form.amount), description: form.description || null, reference: form.reference || null });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('pc.post.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field"><span className="field-label">{t('pc.title')}</span>
          <select className="input" value={form.profitCenterId} onChange={set('profitCenterId')} required>
            <option value="">-</option>
            {centers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 180 }}><span className="field-label">{t('pc.post.type')}</span>
            <select className="input" value={form.type} onChange={set('type')}>
              <option value="REVENUE">{t('pc.type.REVENUE')}</option>
              <option value="EXPENSE">{t('pc.type.EXPENSE')}</option>
            </select>
          </label>
          <label className="field" style={{ maxWidth: 160 }}><span className="field-label">{t('col.amount')}</span>
            <input className="input num" type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} required />
          </label>
        </div>
        <label className="field"><span className="field-label">{t('pc.field.description')}</span>
          <input className="input" value={form.description} onChange={set('description')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
